import { FileDropZone } from "@/components/FileDropzone";
import styled from "@emotion/styled";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import { useField, useFormContext } from "@imminently/decisively-ui/Form";
import { Typography } from "@mui/material";
import clsx from "clsx";
import { useEffect } from "react";

const ImportItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0.5rem;
`;

export type FileData = {
  name: string;
  data: any;
  file?: File; // Store original file for zip handling
};

type FileUploadProps = {
  field: string;
  parseFile: (content: any) => any;
  onClearFile?: () => void;
  className?: string;
  resetKey?: string | number;
};

export const FileUpload = (props: FileUploadProps) => {
  const { field, parseFile, onClearFile, className, resetKey } = props;
  const form = useFormContext();
  const { value: file, error, onValueChange, setValue } = useField<FileData | null>({ name: field });

  const handleFileLoad = (name: string, content: any, file?: File) => {
    try {
      // For zip files, don't parse - just store the data URL
      if (name.endsWith(".zip")) {
        form?.clearErrors?.(field);
        onValueChange({ name, data: { type: "zip", data: content }, file } as any);
        return;
      }
      const parsed = parseFile(content);
      form?.clearErrors?.(field);
      onValueChange({ name, data: parsed, file } as any);
    } catch (err) {
      form?.setError?.(field as any, { message: "Invalid JSON file" });
      console.error("JSON.parse error:", err);
    }
  };

  const clearFile = () => {
    onClearFile?.();
    setValue(null as any);
  };

  useEffect(() => {
    if (file) {
      clearFile();
    }
  }, [resetKey]);

  if (file) {
    return (
      <ImportItem className={className}>
        <Typography>{file.name}</Typography>
        <Button icon
          onClick={clearFile}
          label="Clear"
        >
          <Icon name="delete" />
        </Button>
      </ImportItem>
    );
  }

  return (
    <div className={clsx(className, "flex flex-col gap-2")}>
      {error && <Typography color="error">{error.message?.toString() ?? "File error"}</Typography>}
      <FileDropZone
        className={"flex-1"}
        onLoad={handleFileLoad}
      />
    </div>
  );
};
